import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import useInfiniteDataQuery from '../../../hooks/useInfiniteDataQuery/useInfiniteDataQuery';
import { InfiniteDataQueryFnUtils } from '../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import { paginate } from '../../../lib/nest';
import { CatalogCreationAccessFieldName } from '../../../modules/catalogs/creation/catalogCreationAccess.types';
import { PaginatedResponse } from '../../../types/request.types';
import {
  getFindCatalogAccessEntitiesKey,
  getInfiniteCatalogAccessEntitiesKey,
} from './catalogAccess.keys';
import {
  CATALOG_ACCESS_ENDPOINT_BY_FIELD,
  findCatalogAccessEntities,
} from './catalogAccess.repository';
import { CatalogAccessEntityDTO } from './types/catalogAccess.types';

const INFINITE_CATALOG_ACCESS_LIMIT = 20;

interface UseFindCatalogAccessEntitiesParams {
  catalogId: string;
  field: CatalogCreationAccessFieldName;
  limit: number;
}

interface UseInfiniteCatalogAccessEntitiesParams {
  catalogId: string;
  field: CatalogCreationAccessFieldName;
  isEnabled?: boolean;
}

const getDefaultOrderBy = (field: CatalogCreationAccessFieldName) => ({
  [CATALOG_ACCESS_ENDPOINT_BY_FIELD[field].nameField]: 'ASC' as const,
});

export const useFindCatalogAccessEntities = (
  { catalogId, field, limit }: UseFindCatalogAccessEntitiesParams,
  options: UseDataQueryOptions<PaginatedResponse<CatalogAccessEntityDTO>> = {},
) =>
  useDataQuery<PaginatedResponse<CatalogAccessEntityDTO>>(
    {
      queryKey: getFindCatalogAccessEntitiesKey({ catalogId, field }),
      queryFn: async () => {
        const response = await findCatalogAccessEntities({
          catalogId,
          field,
          params: { skip: 0, limit, ...paginate.order(getDefaultOrderBy(field)) },
        });

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    },
    options,
  );

export const useInfiniteCatalogAccessEntities = ({
  catalogId,
  field,
  isEnabled,
}: UseInfiniteCatalogAccessEntitiesParams) =>
  useInfiniteDataQuery<CatalogAccessEntityDTO>({
    isEnabled,
    maxPages: null,
    limit: INFINITE_CATALOG_ACCESS_LIMIT,
    queryKey: getInfiniteCatalogAccessEntitiesKey({ catalogId, field }),
    defaultOrderBy: getDefaultOrderBy(field),
    queryFn: InfiniteDataQueryFnUtils.fetcherWithCount((params, signal) =>
      findCatalogAccessEntities({ catalogId, field, params, signal }),
    ),
  });
